import { DATE_FORMAT } from '@utils/constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/es';
import { DATETIME_FORMAT } from 'react-big-scheduler-stch';
import { formatDayCard } from '@utils/Functions';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

export const isDateValid = (date) => {
  return dayjs(date)?.isValid();
};

export const formatStartOfDay = (dateFrom) => {
  return isDateValid(dateFrom) ? dayjs(dateFrom).startOf('day').format() : '';
};

export const formatEndOfDay = (dateTo) => {
  return isDateValid(dateTo) ? dayjs(dateTo).endOf('day').format() : '';
};

export const formatRequestDate = (date) => {
  return isDateValid(date) ? dayjs(date).format() : '';
};

export const formatDateForEdit = (date) => {
  return isDateValid(date) ? dayjs(convertDateToLocalTime(date)) : '';
};

export const formatDate = (date, format, language = 'es') => {
  dayjs.locale(language);
  const localDate = convertDateToLocalTime(date);
  return isDateValid(localDate) ? dayjs(localDate).format(format) : '';
};

export const formatReadableDate = (date) => {
  return formatDate(date, DATE_FORMAT.STANDARD);
};

export const formatReadableDateLight = (date) => {
  return formatDate(date, DATE_FORMAT.STANDARD_LIGHT);
};

export const formatReadableDateLightWithDayOfWeek = (date) => {
  return formatDate(date, DATE_FORMAT.STANDARD_LIGHT_WITH_DAY_OF_WEEK);
};

export const formatReadableTime = (date) => {
  return formatDate(date, DATE_FORMAT.READABLE_TIME);
};

export const formatHistoryDate = (date) => {
  return formatDate(date, DATE_FORMAT.HISTORY);
};

export const formatDateToReadableDay = (date) => {
  return formatDate(date, DATE_FORMAT.READABLE_DAY);
};

export const formatDateToCommunication = (date) => {
  return (
    formatDate(date, 'dddd DD') +
    ' de ' +
    formatDate(date, 'MMMM') +
    ' de ' +
    formatDate(date, 'YYYY')
  );
};

export const formatDateToReadableMonth = (date) => {
  return formatDate(date, DATE_FORMAT.READABLE_MONTH);
};

export const formatDateToReadableMonthWithYear = (date) => {
  return formatDate(date, DATE_FORMAT.READABLE_MONTH_WITH_YEAR);
};

export const formatDateToReadableDayFirst = (date) => {
  return formatDate(date, DATE_FORMAT.READABLE_DAY_FIRST);
};

export const formatDateToTime = (date) => {
  return formatDate(date, DATE_FORMAT.TIME);
};

export const formatDateUTC = (date) => {
  return dayjs(date).format(DATE_FORMAT.UTC_DATE);
};

export const formatTimeUTC = (date) => {
  return dayjs(date).format(DATE_FORMAT.UTC_TIME);
};

export const convertDateToLocalTime = (dateToConvert) => {
  if (dateToConvert) {
    const date = dayjs(dateToConvert);
    if (date.isValid()) {
      const dateLocal = dayjs.utc(dateToConvert).local().toDate();
      return dateLocal;
    }
  }
  return null;
};

export const isSameDate = (date1, date2) => {
  if (isDateValid(date1) && isDateValid(date2)) {
    return dayjs(date1).isSame(dayjs(date2), 'day');
  }
};

export const convertToBADate = (date) => {
  return dayjs
    .utc(date)
    .tz('America/Argentina/Buenos_Aires')
    .format('YYYY-MM-DDTHH:mm:ss');
};

export const convertUTCStringToUTCStringBA = (utcString) => {
  return dayjs.tz(utcString, 'America/Argentina/Buenos_Aires').toISOString();
};

export const normalizeFecha = (fecha) => {
  if (!fecha) return '';

  if (fecha.endsWith('Z')) {
    const hasMillis = /\.\d{3}Z$/.test(fecha);
    return hasMillis ? fecha : fecha.replace('Z', '.000Z');
  }

  return fecha.includes('.') ? `${fecha}Z` : `${fecha}.000Z`;
};

export const generateMonthYearList = () => {
  const endDate = dayjs('2018-01-01');
  const currentDate = dayjs();
  const diffMonths = currentDate.diff(endDate, 'month') + 1;

  return {
    options: Array.from({ length: diffMonths }, (_, i) => {
      const date = currentDate.subtract(i, 'month').startOf('month');
      return {
        label: `${date.format('MMMM')} ‘${date.format('YY')}`,
        key: date.format('YYYY/MM/DD[T]HH:mm:ss'),
      };
    }),
  };
};

export const MONTHS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

export const startOfMonth = (date) => {
  return dayjs(date).startOf('month').format(DATE_FORMAT.SCHEDULER);
};

export const endOfMonth = (date) => {
  return dayjs(date).endOf('month').format(DATE_FORMAT.SCHEDULER);
};

export const addOneMonth = (date) => {
  return dayjs(date).add(1, 'month').format(DATE_FORMAT.SCHEDULER);
};

export const addOneWeek = (date) => {
  return dayjs(date).add(7, 'days').format(DATE_FORMAT.SCHEDULER);
};

export const addOneYear = (date) => {
  return dayjs(date).add(1, 'year').format(DATE_FORMAT.SCHEDULER);
};

export const subtractOneMonth = (date) => {
  return dayjs(date).subtract(1, 'month').format(DATE_FORMAT.SCHEDULER);
};

export const subtractOneWeek = (date) => {
  return dayjs(date).subtract(7, 'days').format(DATE_FORMAT.SCHEDULER);
};

export const subtractOneYear = (date) => {
  return dayjs(date).subtract(1, 'year').format(DATE_FORMAT.SCHEDULER);
};

export function previousSunday(fecha) {
  return dayjs(fecha).day(0).startOf('day').format(DATE_FORMAT.SCHEDULER);
}

export function nextSaturday(fecha) {
  return dayjs(fecha).day(6).endOf('day').format(DATE_FORMAT.SCHEDULER);
}

export function firstDayOfTheYear(date) {
  const year = date?.year();
  const firstDay = dayjs(`${year}-01-01`);
  return firstDay.format(DATETIME_FORMAT);
}

export function lastDayOfTheYear(date) {
  const year = date.year();
  const lastDay = formatEndOfDay(
    dayjs(`${year}-12-31`).format(DATETIME_FORMAT),
  );
  return lastDay;
}

export const isValidDate = (value) => {
  const selectedDate = dayjs(value);
  return !selectedDate.isValid();
};

export const isDatePast = (value) =>
  dayjs(value).isBefore(dayjs().subtract(1, 'day'));

export const isOneMonthPassed = (value) =>
  dayjs(value).isBefore(dayjs().subtract(1, 'month'));

export const isOneYearLater = (value) => {
  const currentDate = dayjs();
  const nextYear = currentDate.add(1, 'year');
  const selectedDate = dayjs(value);
  return selectedDate.isAfter(nextYear, 'day');
};

export const formatDateEndOfDay = (date) => {
  return dayjs(date).isValid() ? dayjs(date).endOf('day').format() : '';
};

export const formatDateStartOfDay = (date) => {
  return dayjs(date).isValid() ? dayjs(date).startOf('day').format() : '';
};

export const combineFechaHora = (fecha, hora) => {
  if (!fecha || !hora) return null;

  const datePart = dayjs.utc(fecha);
  const timePart = dayjs.utc(hora);

  if (!datePart.isValid() || !timePart.isValid()) return null;

  const combined = datePart
    .hour(timePart.hour())
    .minute(timePart.minute())
    .second(timePart.second())
    .millisecond(timePart.millisecond());

  return combined.toISOString();
};

export const combineFechaHoraBeforeRequest = (fecha, hora) => {
  if (!fecha || !hora) return null;

  const datePart = dayjs.utc(fecha);
  const timePart = dayjs.utc(hora);

  if (!datePart.isValid() || !timePart.isValid()) return null;

  const hour = timePart.hour();
  const baseDate = hour >= 0 && hour < 3 ? datePart.add(1, 'day') : datePart;

  const combined = baseDate
    .hour(timePart.hour())
    .minute(timePart.minute())
    .second(timePart.second())
    .millisecond(timePart.millisecond());

  return combined.toISOString();
};

export const getApprovalLimitDate = (inputDate) => {
  const day = dayjs(inputDate).date();
  if (day >= 0 && day <= 11) return dayjs().set('date', 14);
  return dayjs().add(1, 'month').set('date', 14);
};

export const getApprovalLimitDateDayjs = () => {
  const today = dayjs();
  const day = today.date();

  if (day >= 0 && day <= 11) {
    return dayjs()
      .subtract(1, 'month')
      .set('date', 11)
      .set('hours', 0)
      .set('minutes', 0)
      .set('seconds', 0);
  } else {
    return dayjs()
      .set('date', 11)
      .set('hours', 0)
      .set('minutes', 0)
      .set('seconds', 0);
  }
};

export const getSameDate = (from_date, to_date) => {
  const isSameDay = dayjs(from_date).isSame(to_date, 'day');
  let dates = formatDayCard(from_date);
  dates += to_date && !isSameDay ? ` • ${formatDayCard(to_date)}` : '';
  return dates;
};

export const errorFilterDatePickerPaychecks = (query) => {
  const isBefore = dayjs(query?.from_date).isBefore(query?.to_date);
  if (!isBefore && query?.from_date && query?.to_date) {
    return '* La fecha "Desde" debe ser menor o igual a la fecha "Hasta" ';
  }
  return '';
};
